import { useState } from 'react';
import { Lightbulb, Sparkles } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import type { ClassifiedEntry } from '@/lib/api-client';
import { previewText } from '@/lib/markdown/previewText';

export interface DeepDiveCardProps {
  entry: ClassifiedEntry;
  /** Open the lesson generator with this question (§2.8 #91). */
  onMakeLesson: (entry: ClassifiedEntry) => void;
}

/**
 * A chat question the classifier judged worth a lesson of its own - shown as
 * a suggestion, not as one more row.
 *
 * It used to share a single line with its date and its button, which left
 * the question about 120px wide on a phone and made the most actionable
 * thing in the tab read like a footnote. Now: the question at full width,
 * the start of the answer the tutor gave, and "Make a lesson" as a real
 * button under them (full width on a phone, in thumb reach).
 *
 * The faint brand (lavender) tint marks it as a suggestion - something on offer - and
 * sets it apart from the attachments above, which are the learner's own.
 */
export function DeepDiveCard({ entry, onMakeLesson }: DeepDiveCardProps) {
  const [open, setOpen] = useState(false);
  const preview = previewText(entry.answer ?? '');

  return (
    <article className='rounded-xl border border-brand-200/70 bg-brand-50/70 p-4 dark:border-primary/25 dark:bg-primary/10'>
      <div className='flex items-start gap-3'>
        <Lightbulb
          className='mt-0.5 h-4 w-4 flex-shrink-0 text-brand-600 dark:text-primary'
          aria-hidden='true'
        />
        <div className='min-w-0 flex-1'>
          <h4 className='text-[15px] font-medium leading-snug text-foreground sm:text-sm'>
            {entry.question}
          </h4>
          {preview && (
            <p
              className={
                open
                  ? 'mt-1.5 text-sm leading-relaxed text-muted-foreground'
                  : 'mt-1.5 line-clamp-3 text-sm leading-relaxed text-muted-foreground'
              }
            >
              {preview}
            </p>
          )}
          {preview.length > 160 && (
            <button
              type='button'
              aria-expanded={open}
              onClick={() => setOpen(v => !v)}
              className='mt-1 min-h-11 text-xs font-medium text-brand-700 dark:text-primary sm:min-h-0'
            >
              {open ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      </div>
      <Button
        type='button'
        variant='outline'
        onClick={() => onMakeLesson(entry)}
        title='Open the lesson generator with this question pre-filled'
        className='mt-3 h-11 w-full bg-card sm:ml-7 sm:h-8 sm:w-auto'
      >
        <Sparkles
          className='h-4 w-4 text-brand-600 dark:text-primary'
          aria-hidden='true'
        />
        Make a lesson
      </Button>
    </article>
  );
}
